import React, { useId, useMemo, useState } from 'react';
import type {
  McpProposalPreview,
  McpServerCandidate,
  McpProvisionRequest,
} from '../../types';
import McpTrustSignals from './McpTrustSignals';
import McpCredentialFields from './McpCredentialFields';

/** Per-card lifecycle, mirroring PreviewCard but for the provisioning gate. */
export type McpProposalState =
  | 'pending'
  | 'provisioning'
  | 'provisioned'
  | 'declined'
  | 'superseded';

interface McpProposalCardProps {
  preview: McpProposalPreview;
  state: McpProposalState;
  /** Default scope for a newly provisioned server. 'project' per locked decision. */
  defaultScope: 'project' | 'user';
  /** Approve: carries the chosen scope + credentials entered after approval intent. */
  onApprove: (req: McpProvisionRequest) => void;
  /** Pick a different candidate from the top-3 shortlist (by reverse-DNS name). */
  onSelectCandidate: (registryName: string) => void;
  onDecline: () => void;
}

/**
 * McpProposalCard (AC-1738, epic AC-1732). The human-approval gate for a Tier-3
 * remote-MCP proposal. The assistant has resolved a capability need to a
 * specific remote server (plus a ranked shortlist); the user reviews the
 * registry trust signals, picks a scope, enters a credential ONLY if required,
 * and must explicitly Approve before anything is provisioned. Declining leaves
 * zero residual state.
 *
 * Built as a PreviewCard-shaped variant (same accessible role="group" region,
 * text-not-color status, >=44px tap targets) but for the propose_mcp_server
 * action rather than create/update. Server state (the proposal + provisioning
 * result) is owned by React Query in the parent; this card only holds the
 * ephemeral form state (chosen scope, entered secret).
 *
 * State drives affordances:
 *   - 'provisioning' : Approve/Decline disabled + aria-busy on the container.
 *   - 'provisioned'  : a provisioned indicator replaces the actions.
 *   - 'declined'     : a dimmed "Declined" note.
 *   - 'superseded'   : Approve disabled (a newer turn replaced this proposal).
 */
const McpProposalCard: React.FC<McpProposalCardProps> = ({
  preview,
  state,
  defaultScope,
  onApprove,
  onSelectCandidate,
  onDecline,
}) => {
  const headingId = useId();
  const messageId = useId();
  const scopeName = useId();

  const candidate = preview.candidate;
  const [scope, setScope] = useState<'project' | 'user'>(defaultScope);
  const [credentials, setCredentials] = useState<
    { name: string; value: string }[]
  >([]);

  const isProvisioning = state === 'provisioning';
  const isProvisioned = state === 'provisioned';
  const isDeclined = state === 'declined';
  const isSuperseded = state === 'superseded';

  // M5a: gate Approve on a real credential when the candidate requires auth.
  // The HTML `required`/`aria-required` on the credential input is cosmetic for
  // a type="button" Approve (no form submit), so without this a user could
  // approve a requires_auth candidate with an EMPTY token -- which the backend
  // declines and previously burned the deferred build. McpCredentialFields emits
  // an empty `credentials` array when the field is blank, and a single
  // {name, value} entry once typed; here we additionally require the value to be
  // non-empty after trimming so a whitespace-only token does not satisfy the
  // gate. The optional (requires_auth === false) case stays approvable with an
  // empty field.
  const hasRequiredCredential = useMemo(
    () => credentials.some((c) => c.value.trim().length > 0),
    [credentials],
  );
  const isMissingRequiredCredential =
    !!candidate.requires_auth && !hasRequiredCredential;

  // Alternatives = the shortlist minus the currently-selected candidate, so the
  // top pick is shown once (in the detail area) and only the OTHER options are
  // offered as selectable buttons.
  const alternatives = useMemo(
    () =>
      preview.shortlist.filter(
        (c) => c.registry_name !== candidate.registry_name,
      ),
    [preview.shortlist, candidate.registry_name],
  );

  const handleApprove = () => {
    const req: McpProvisionRequest = {
      scope,
      // AC-1800: the credential field is now always offered (required when the
      // candidate requires auth, optional otherwise). McpCredentialFields emits
      // an empty array when the optional field is left blank, so whatever the
      // user actually entered flows through here verbatim.
      credentials,
    };
    onApprove(req);
  };

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      aria-describedby={messageId}
      aria-live="polite"
      aria-busy={isProvisioning ? 'true' : undefined}
      className={`mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-3 focus:outline-none ${
        isDeclined ? 'opacity-50' : ''
      }`}
    >
      <div>
        <h4
          id={headingId}
          className="text-sm font-semibold text-gray-900 dark:text-warm-white"
        >
          Add a remote integration?
        </h4>
        {/* id-free "why this matched" explanation. */}
        <p
          id={messageId}
          className="text-xs text-gray-600 dark:text-gray-400 mt-0.5"
        >
          {preview.message}
        </p>
      </div>

      {/* Top candidate detail */}
      <div className="flex flex-col gap-1">
        <div
          className="text-sm font-medium text-gray-900 dark:text-warm-white break-words"
          title={candidate.display_name}
        >
          {candidate.display_name}
        </div>
        {candidate.description && (
          <p className="text-xs text-gray-700 dark:text-gray-300 break-words">
            {candidate.description}
          </p>
        )}
        <McpTrustSignals candidate={candidate} />
      </div>

      {/* Shortlist alternatives (up to the remaining of top-3). Selecting one
          re-proposes that candidate by its reverse-DNS registry_name. */}
      {alternatives.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
            Other matches
          </div>
          <ul className="flex flex-col gap-1">
            {alternatives.map((alt: McpServerCandidate) => (
              <li key={alt.registry_name}>
                <button
                  type="button"
                  onClick={() => onSelectCandidate(alt.registry_name)}
                  disabled={isProvisioning || isProvisioned || isDeclined}
                  className="w-full min-h-[44px] text-left text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {/* Name only: the official/community trust word is shown for
                      the SELECTED candidate's trust signals, kept off the
                      alternative buttons so a single trust label renders. */}
                  <span className="font-medium">{alt.display_name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Scope selector: defaults to project, but always asks. Rendered as a
          radiogroup (not a <fieldset>, whose implicit role="group" would collide
          with the card's own role="group" region). */}
      <div
        role="radiogroup"
        aria-label="Where should this live?"
        className="flex flex-col gap-1"
      >
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
          Where should this live?
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
          <input
            type="radio"
            name={scopeName}
            value="project"
            checked={scope === 'project'}
            onChange={() => setScope('project')}
            disabled={isProvisioning || isProvisioned || isDeclined}
          />
          This project
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
          <input
            type="radio"
            name={scopeName}
            value="user"
            checked={scope === 'user'}
            onChange={() => setScope('user')}
            disabled={isProvisioning || isProvisioned || isDeclined}
          />
          My user account
        </label>
      </div>

      {/* Authentication note: when the candidate requires auth, tell the user a
          credential is needed before the field. AC-1800. */}
      {candidate.requires_auth && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          This integration needs authentication — enter an API key / token below.
        </p>
      )}

      {/* Credential entry: rendered for every remote candidate after the user has
          seen the approval intent (required when requires_auth, optional
          otherwise). Never pre-filled. AC-1800. */}
      <McpCredentialFields candidate={candidate} onChange={setCredentials} />

      {/* Actions / status (text, not color-only). */}
      {isProvisioned ? (
        <div className="text-xs font-medium text-green-700 dark:text-green-400">
          Provisioned — added to your integrations
        </div>
      ) : isDeclined ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Declined — nothing was added
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleApprove}
            disabled={
              isProvisioning || isSuperseded || isMissingRequiredCredential
            }
            aria-disabled={isMissingRequiredCredential ? 'true' : undefined}
            className="w-full min-h-[44px] text-sm font-medium bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Approve &amp; add
          </button>
          <button
            type="button"
            onClick={onDecline}
            disabled={isProvisioning}
            className="w-full min-h-[44px] text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Decline
          </button>
        </div>
      )}
    </div>
  );
};

export default McpProposalCard;
